import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { getIsPriceGridFavoritesOnly, getPriceGridFavoriteSymbolsFilter } from '../favorites';
import type { PriceGridFilterConfig, PriceGridFilterState, PriceGridSourceItem } from '../types';

function getBasePairKey(sourceItem: PriceGridSourceItem) {
  const { BaseCurrency: baseCurrency, QuoteCurrency: quoteCurrency } = sourceItem.security;
  return baseCurrency && quoteCurrency ? `${baseCurrency}-${quoteCurrency}` : undefined;
}

function matchesMarkets(sourceItem: PriceGridSourceItem, markets: string[] | undefined) {
  const selectedMarkets = markets ?? [];
  if (selectedMarkets.length === 0) {
    return true;
  }

  const securityMarkets = sourceItem.security.Markets ?? [];
  return selectedMarkets.some(market => securityMarkets.includes(market));
}

function matchesCurrencies(sourceItem: PriceGridSourceItem, currencies: string[] | undefined) {
  const selectedCurrencies = currencies ?? [];
  if (selectedCurrencies.length === 0) {
    return true;
  }

  const { BaseCurrency: baseCurrency, QuoteCurrency: quoteCurrency } = sourceItem.security;
  return selectedCurrencies.some(currency => currency === baseCurrency || currency === quoteCurrency);
}

function matchesBasePairs(sourceItem: PriceGridSourceItem, basePairs: string[] | undefined) {
  const selectedBasePairs = basePairs ?? [];
  if (selectedBasePairs.length === 0) {
    return true;
  }

  const basePairKey = getBasePairKey(sourceItem);
  return basePairKey != null && selectedBasePairs.includes(basePairKey);
}

export function matchesPriceGridSourceFiltersWithoutProductTypes<TSource extends PriceGridSourceItem>(
  sourceItem: TSource,
  filterState: PriceGridFilterState
) {
  const { markets, currencies, basePairs, symbols } = filterState;
  const favoriteSymbols = getPriceGridFavoriteSymbolsFilter(filterState);

  if (!matchesMarkets(sourceItem, markets)) {
    return false;
  }

  if (!matchesCurrencies(sourceItem, currencies)) {
    return false;
  }

  if (!matchesBasePairs(sourceItem, basePairs)) {
    return false;
  }

  if (getIsPriceGridFavoritesOnly(filterState) && !favoriteSymbols.includes(sourceItem.security.Symbol)) {
    return false;
  }

  if (symbols && symbols.length > 0 && !symbols.includes(sourceItem.security.Symbol)) {
    return false;
  }

  return true;
}

export function defaultPriceGridSourceFilter<TSource extends PriceGridSourceItem>(
  sourceItem: TSource,
  filterState: PriceGridFilterState
) {
  const { productTypes } = filterState;

  if (productTypes && productTypes.length > 0 && !productTypes.includes(sourceItem.security.ProductType)) {
    return false;
  }

  return matchesPriceGridSourceFiltersWithoutProductTypes(sourceItem, filterState);
}

export function filterPriceGridSourceItems<TSource extends PriceGridSourceItem>({
  sourceItems,
  filterState,
  filterConfig,
}: {
  sourceItems: TSource[];
  filterState: PriceGridFilterState;
  filterConfig?: PriceGridFilterConfig<TSource>;
}) {
  const clientFilter = filterConfig?.clientFilter ?? defaultPriceGridSourceFilter;

  return sourceItems.filter(sourceItem => clientFilter(sourceItem, filterState));
}

export function usePriceGridFilter<TSource extends PriceGridSourceItem>({
  filterConfig,
  filterState: persistedFilterState,
  onFilterStateChanged,
}: {
  filterConfig?: PriceGridFilterConfig<TSource>;
  filterState?: PriceGridFilterState;
  onFilterStateChanged?: (filterState: PriceGridFilterState) => void;
}) {
  // The persisted filter state seeds local state only; subsequent changes are owned locally and reported out.
  const [filterState, setFilterState] = useState<PriceGridFilterState>(
    persistedFilterState ?? filterConfig?.initialState ?? {}
  );

  // Defer filter application to the data pipeline so toolbar typing does not re-render PriceGrid
  // before Kyoko's onNewColumnsLoaded can persist column dynamics saved via the header dialog.
  const deferredFilterState = useDeferredValue(filterState);

  const setFilterStateAndNotify = useCallback(
    (nextFilterState: PriceGridFilterState) => {
      setFilterState(nextFilterState);
      onFilterStateChanged?.(nextFilterState);
    },
    [onFilterStateChanged]
  );

  const filterSourceItems = useCallback(
    (sourceItems: TSource[]) =>
      filterPriceGridSourceItems({
        sourceItems,
        filterState: deferredFilterState,
        filterConfig,
      }),
    [deferredFilterState, filterConfig]
  );

  const setSearch = useCallback(
    (search: string) => {
      setFilterStateAndNotify({
        ...filterState,
        search: search.length === 0 ? undefined : search,
      });
    },
    [filterState, setFilterStateAndNotify]
  );

  return useMemo(
    () => ({
      filterState,
      setFilterState: setFilterStateAndNotify,
      setSearch,
      filterSourceItems,
    }),
    [filterSourceItems, filterState, setFilterStateAndNotify, setSearch]
  );
}
